/**
 * Component: SimpleSearchPage
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R01, FRONTEND-WEB-V1-R02, FRONTEND-WEB-V1-R09,
 *               FRONTEND-WEB-V1-R10, FRONTEND-WEB-V1-R13,
 *               SEARCH-SIMPLE-V1-R01, SEARCH-SIMPLE-V1-R02,
 *               SEARCH-SIMPLE-V1-R03, SEARCH-SIMPLE-V1-R04, SEARCH-SIMPLE-V1-R07
 *
 * Simple Search interface for keyword-based retrieval only.
 * Performs lexical keyword matching without semantic embeddings.
 *
 * SEARCH-SIMPLE-V1-R01: Keyword-based retrieval without semantic scoring.
 * SEARCH-SIMPLE-V1-R02: Advanced mode with Boolean operators and phrase matching.
 * SEARCH-SIMPLE-V1-R03: Results ordered by BM25 score or lexical relevance.
 * SEARCH-SIMPLE-V1-R04: Display results with title, excerpt, highlights, score.
 * FRONTEND-WEB-V1-R02: Collect user input via text or optional audio.
 * Note: SEARCH-SIMPLE-V1-R05 (pass-to-chat, cross-mode comparison) deferred to V2.
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useRef } from 'react';
import { Search, Loader2, AlertCircle, Info } from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';
import { trackUsageAction } from '@/lib/usage-tracking';
import { useIsFeatureEnabled } from '@/contexts';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UserInputArea } from '@/components/input/UserInputArea';
import {
  SearchFilterSidebar,
  SearchFilterMobileButton,
  INITIAL_FILTERS,
  type SearchFilters,
  type Facet,
} from './components/SearchFilterSidebar';
import { toFilterRequest, hasActiveFilters } from './components/search-filter-types';
import { useFilterAutoSearch } from './useFilterAutoSearch';
import { SearchPagination } from './components/SearchPagination';
import { SearchResultCard } from './components/SearchResultCard';
import { ResultDetailPanel } from './components/ResultDetailPanel';
import { normalizeQuotes } from './components/search-result-utils';

const DEFAULT_PAGE_SIZE = 20;

// API Types
interface SimpleSearchResult {
  document_id: string;
  title: string;
  content: string;
  score: number;
  keyword_score: number;
  highlights: Record<string, string[]>;
  metadata: Record<string, unknown>;
  source_url: string | null;
}

interface SimpleSearchResponse {
  results: SimpleSearchResult[];
  total_count: number | null;
  facets: Facet[];
  query: string;
}


/**
 * SimpleSearchPage Component
 *
 * FRONTEND-WEB-V1-R01: Support Simple Search as one of four primary modes.
 * SEARCH-SIMPLE-V1-R01: Keyword-based retrieval only.
 * SEARCH-SIMPLE-V1-R03: Results ordered by keyword relevance (BM25).
 */
export function SimpleSearchPage(): React.ReactElement {
  // SPEECH-V1-R02 / Bug #4340: hide the mic affordance entirely when the
  // speech module is not enabled for this deployment.
  const isSpeechEnabled = useIsFeatureEnabled('speech');

  // State
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SimpleSearchResult[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  // Bug #5060: the old Basic/Advanced toggle drove a `use_boolean` flag the
  // backend never consumed (Boolean operators always work). Replaced with an
  // info icon that reveals the Boolean-syntax help on demand.
  const [showBooleanHelp, setShowBooleanHelp] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // SEARCH-SIMPLE-V1-R07: server-side faceted filtering state
  const [filters, setFilters] = useState<SearchFilters>(INITIAL_FILTERS);
  const [facets, setFacets] = useState<Facet[]>([]);
  const resultsTopRef = useRef<HTMLDivElement>(null);
  // Bug #3878: State for detail panel
  const [detailSectionId, setDetailSectionId] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  /**
   * Execute simple search request with pagination.
   * SEARCH-SIMPLE-V1-R01: Perform keyword-based retrieval.
   * SEARCH-SEMANTIC-V1-R03: Paginated result presentation.
   */
  const handleSearch = useCallback(
    async (e?: React.FormEvent, page = 1) => {
      e?.preventDefault();

      if (!query.trim()) {
        return;
      }

      setIsLoading(true);
      setError(null);
      setHasSearched(true);

      try {
        const skip = (page - 1) * DEFAULT_PAGE_SIZE;
        // SEARCH-SIMPLE-V1-R07: send the user's refine selections (facets, date
        // range, recommendations-only, figures/tables scope) for server-side
        // filtering.
        const response = await api.post<SimpleSearchResponse>('/search/simple', {
          query: normalizeQuotes(query.trim()),
          include_highlights: true,
          top_n: DEFAULT_PAGE_SIZE,
          skip,
          ...toFilterRequest(filters),
          limit_to_figures_tables: filters.limitToFiguresAndTables,
          prioritize_recent: filters.prioritizeRecent,
        });

        setResults(response.data.results);
        setTotalCount(response.data.total_count);
        setFacets(response.data.facets ?? []);
        setCurrentPage(page);

        // FRONTEND-WEB-V1-R10: Track search query for usage metrics
        void trackUsageAction('search_query', {
          query_text: query.trim(),
          mode: 'simple',
        });
      } catch (err) {
        setError(getErrorMessage(err));
        setResults([]);
        setTotalCount(null);
        setFacets([]);
      } finally {
        setIsLoading(false);
      }
    },
    [query, filters]
  );

  // SEARCH-SIMPLE-V1-R07 (Bug #5052): server-side filtering re-runs the search
  // (debounced) when a refine selection changes, once a search has been run.
  useFilterAutoSearch(filters, hasSearched, () => void handleSearch(undefined, 1));

  const handlePageChange = useCallback(
    (page: number) => {
      void handleSearch(undefined, page);
      resultsTopRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
    [handleSearch]
  );

  /**
   * Handle link open: open detail panel for the clicked result.
   * Bug #3878: Opens detail panel with section content.
   * COUNTER tracking is handled by SearchResultCard internally.
   */
  const handleLinkOpen = useCallback(
    (_documentId: string, _rankingPosition: number, guidelineSectionId: number | null) => {
      if (guidelineSectionId != null) {
        setDetailSectionId(guidelineSectionId);
        setDetailOpen(true);
      }
    },
    []
  );

  // SEARCH-SIMPLE-V1-R07 (Bug #5053): keep the refine sidebar visible when a
  // search has run and there are results OR active filters, so a zero-result
  // filter can still be cleared.
  const showFilters =
    hasSearched && (results.length > 0 || hasActiveFilters(filters));

  return (
    <div className="mx-auto max-w-4xl">
      {/* Page Header */}
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Simple Search</h1>
        <p className="mt-2 text-muted-foreground">
          Search for exact keyword matches in clinical guidelines using traditional
          lexical search. You can type or use voice input.
          {/* Bug #5060: info icon revealing Boolean-syntax help on demand */}
          <button
            type="button"
            onClick={() => setShowBooleanHelp((v) => !v)}
            className="ml-1.5 inline-flex items-center align-text-bottom rounded text-primary hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-expanded={showBooleanHelp}
            aria-label="Boolean search syntax help"
            data-testid="boolean-help-toggle"
          >
            <Info className="h-4 w-4" aria-hidden="true" />
          </button>
        </p>
        {showBooleanHelp && (
          <div
            className="mt-3 max-w-xl rounded-lg border bg-muted/50 px-3 py-2 text-xs text-muted-foreground"
            data-testid="boolean-help"
          >
            <p className="font-medium text-foreground">Boolean search syntax:</p>
            <ul className="mt-1 list-inside list-disc space-y-0.5">
              <li><code className="rounded bg-muted px-1">AND</code> &mdash; both terms must match (e.g. <em>hypertension AND treatment</em>)</li>
              <li><code className="rounded bg-muted px-1">OR</code> &mdash; either term matches (e.g. <em>aspirin OR clopidogrel</em>)</li>
              <li><code className="rounded bg-muted px-1">NOT</code> &mdash; exclude term (e.g. <em>diabetes NOT type1</em>)</li>
              <li><code className="rounded bg-muted px-1">&quot;quotes&quot;</code> &mdash; exact phrase (e.g. <em>&quot;blood pressure target&quot;</em>)</li>
            </ul>
          </div>
        )}
      </header>

      {/* Search Form - FRONTEND-WEB-V1-R02: Text and optional audio input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSearch();
        }}
        className="mb-8 space-y-4"
        role="search"
        aria-label="Simple keyword search"
      >
        {/* FRONTEND-WEB-V1-R13: Responsive layout - stack on mobile, row on desktop */}
        <div className="flex flex-col gap-4 md:flex-row">
          {/* FRONTEND-WEB-V1-R02: UserInputArea with audio support */}
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10"
              aria-hidden="true"
            />
            <div className="pl-8">
              <UserInputArea
                value={query}
                onChange={setQuery}
                onSubmit={() => void handleSearch()}
                placeholder="Enter keywords to search"
                disabled={isLoading}
                enableAudio={isSpeechEnabled}
                multiline={false}
                ariaLabel="Search keywords"
              />
            </div>
          </div>
          {/* FRONTEND-WEB-V1-R13: Full-width button on mobile for touch-friendly target */}
          <Button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="w-full md:w-auto min-h-touch"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Searching...
              </>
            ) : (
              'Search'
            )}
          </Button>
        </div>
      </form>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6" role="alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* SEARCH-SIMPLE-V1-R07: Mobile filter button */}
      {showFilters && (
        <div className="md:hidden">
          <SearchFilterMobileButton
            filters={filters}
            onFiltersChange={setFilters}
            onReset={() => setFilters(INITIAL_FILTERS)}
            facets={facets}
            showFiguresToggle
            resultCount={results.length}
          />
        </div>
      )}

      {/* Results area: sidebar on desktop + results */}
      <div ref={resultsTopRef} className={showFilters ? 'md:flex md:gap-6' : ''}>
        {/* SEARCH-SIMPLE-V1-R07: Desktop filter sidebar */}
        {showFilters && (
          <aside className="hidden md:block w-56 shrink-0" aria-label="Search filters">
            <div className="sticky top-4">
              <SearchFilterSidebar
                filters={filters}
                onFiltersChange={setFilters}
                onReset={() => setFilters(INITIAL_FILTERS)}
                facets={facets}
                showFiguresToggle
                resultCount={results.length}
              />
            </div>
          </aside>
        )}

        <div className="min-w-0 flex-1">
          {/* Results Summary */}
          {hasSearched && !isLoading && !error && (
            <div className="mb-4 text-sm text-muted-foreground" aria-live="polite">
              {totalCount !== null && totalCount > 0 ? (
                <span>
                  Found {totalCount} result{totalCount !== 1 ? 's' : ''}
                  {totalCount > DEFAULT_PAGE_SIZE &&
                    ` (page ${currentPage} of ${Math.ceil(totalCount / DEFAULT_PAGE_SIZE)})`}
                </span>
              ) : results.length > 0 ? (
                <span>Showing {results.length} results</span>
              ) : (
                <span>No results found for &quot;{query}&quot;</span>
              )}
            </div>
          )}

          {/* Results List */}
          <section
            aria-label="Search results"
            className="space-y-4"
            aria-busy={isLoading}
          >
            {results.map((result, index) => (
              <SearchResultCard
                key={result.document_id}
                result={result}
                rank={(currentPage - 1) * DEFAULT_PAGE_SIZE + index + 1}
                onLinkOpen={handleLinkOpen}
                query={query}
                searchMode="simple"
              />
            ))}
          </section>

          {/* SEARCH-SEMANTIC-V1-R03: Pagination controls */}
          {totalCount !== null && totalCount > 0 && (
            <SearchPagination
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={handlePageChange}
              isLoading={isLoading}
            />
          )}

          {/* Empty State */}
          {hasSearched && !isLoading && results.length === 0 && !error && (
            <div className="py-12 text-center text-muted-foreground">
              <Search className="mx-auto mb-4 h-12 w-12 opacity-50" aria-hidden="true" />
              <p className="text-lg">No results found</p>
              <p className="mt-2">
                Try different keywords or check your spelling. Simple search matches
                exact terms only.
              </p>
            </div>
          )}

          {/* Initial State */}
          {!hasSearched && (
            <div className="py-12 text-center text-muted-foreground">
              <Search className="mx-auto mb-4 h-12 w-12 opacity-50" aria-hidden="true" />
              <p className="text-lg">Enter keywords to search</p>
              <p className="mt-2">
                Simple Search finds exact keyword matches in clinical guideline
                documents using traditional lexical search.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Bug #3878: Detail panel for View Full Result */}
      <ResultDetailPanel
        guidelineSectionId={detailSectionId}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
}

export default SimpleSearchPage;
